import { useLayoutEffect, useState } from "react";

/**
 * Builder-width breakpoints (plan Part 4 / 5.4).
 *
 * The prototype used viewport media queries at 1050 / 760 / 620 px. Inside the admin shell
 * the app sidebar eats 112–312 px, so the same thresholds are measured against the builder
 * root's own width with a ResizeObserver and exposed as `data-eb-bp` on the root:
 *
 *   > 1050  wide
 *   ≤ 1050  mid
 *   ≤ 760   narrow
 *   ≤ 620   phone
 *
 * The attribute is written straight onto the DOM node inside the layout effect (before
 * paint) so the first frame never renders with the wrong breakpoint, and also mirrored in
 * React state for components that need to branch (e.g. the mobile section dropdown).
 *
 * The same observer measures the app's mobile bottom bar (`.mob_bottom_bar`, visible below
 * 992px) and publishes its height as `--eb-mobile-bar-offset` so the fixed builder footer
 * and toast sit above it (Part 4, "measure in Phase 1").
 */

export const EB_BREAKPOINTS = { MID: 1050, NARROW: 760, PHONE: 620 };

export const breakpointForWidth = (width) => {
  if (width <= EB_BREAKPOINTS.PHONE) return "phone";
  if (width <= EB_BREAKPOINTS.NARROW) return "narrow";
  if (width <= EB_BREAKPOINTS.MID) return "mid";
  return "wide";
};

const measureMobileBar = () => {
  if (typeof document === "undefined") return 0;
  const bar = document.querySelector(".mob_bottom_bar");
  if (!bar) return 0;
  // display:none (d-lg-none at ≥992px) → offsetParent is null and offsetHeight is 0
  if (bar.offsetParent === null) return 0;
  return bar.offsetHeight || 0;
};

const useBuilderBreakpoint = (rootRef) => {
  const [bp, setBp] = useState("wide");

  useLayoutEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;

    let frame = null;
    const apply = () => {
      frame = null;
      const width = node.getBoundingClientRect().width;
      const next = breakpointForWidth(width);
      if (node.getAttribute("data-eb-bp") !== next) {
        node.setAttribute("data-eb-bp", next);
      }
      setBp((prev) => (prev === next ? prev : next));
      node.style.setProperty("--eb-mobile-bar-offset", `${measureMobileBar()}px`);
    };
    const schedule = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(apply);
    };

    apply();

    let observer = null;
    if (typeof window.ResizeObserver === "function") {
      observer = new window.ResizeObserver(schedule);
      observer.observe(node);
    }
    window.addEventListener("resize", schedule);

    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
      window.removeEventListener("resize", schedule);
    };
  }, [rootRef]);

  return bp;
};

export default useBuilderBreakpoint;
